import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Wallet, Send, CreditCard, ScrollText, Loader2, ArrowDownCircle,
  ArrowUpCircle, Clock, RefreshCw, Gift,
} from 'lucide-react';
import { toast } from 'sonner';

// رصيد التعلّم — وحدة الرصيد التربوية
const UNIT = 'د.أ';

const TXN_LABELS = {
  request: 'طلب شحن',
  recharge: 'شحن ببطاقة',
  gift: '🎁 هدية من المعلم',
  adjust: 'إضافة من الدعم الفني',
  deduct: 'خصم من الدعم الفني',
  purchase: 'شراء',
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('ar-EG', { dateStyle: 'short', timeStyle: 'short' }); }
  catch { return iso; }
};

const StudentWallet = ({ apiUrl, token, prefillCode }) => {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reqAmount, setReqAmount] = useState('');
  const [reqRef, setReqRef] = useState('');
  const [reqNote, setReqNote] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const redeemInputRef = useRef(null);

  const authHeaders = useMemo(
    () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }),
    [token],
  );

  // A recharge-card code tapped from the chat is auto-pasted into the charge field.
  useEffect(() => {
    const c = (prefillCode || '').trim();
    if (!c) return;
    setCode(c.toUpperCase());
    // Focus + highlight so the student just presses "شحن الرصيد".
    requestAnimationFrame(() => {
      try {
        redeemInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        redeemInputRef.current?.focus();
      } catch { /* ignore */ }
    });
  }, [prefillCode]);

  const fetchBalance = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/student/balance`, { headers: authHeaders });
      if (r.ok) {
        const data = await r.json();
        setBalance(data.balance || 0);
        setTransactions(data.transactions || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, [apiUrl, authHeaders]);

  useEffect(() => { fetchBalance(); }, [fetchBalance]);

  const requestBalance = async () => {
    const amount = Number(reqAmount);
    if (!amount || amount <= 0) { toast.error('أدخل قيمة صحيحة'); return; }
    if (!reqRef.trim()) { toast.error('أدخل الرقم المرجعي (الرقم أو الاسم الذي حوّلت منه)'); return; }
    setRequesting(true);
    try {
      const r = await fetch(`${apiUrl}/api/student/balance/request`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ amount, note: reqNote, reference_number: reqRef.trim() }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success('تم إرسال طلب الشحن إلى الدعم الفني');
        setReqAmount(''); setReqRef(''); setReqNote('');
        fetchBalance();
      } else toast.error(data.detail || 'فشل إرسال الطلب');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setRequesting(false);
  };

  const redeemCard = async () => {
    const c = code.trim();
    if (!c) { toast.error('أدخل كود البطاقة'); return; }
    setRedeeming(true);
    try {
      const r = await fetch(`${apiUrl}/api/student/balance/redeem`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify({ code: c }),
      });
      const data = await r.json();
      if (r.ok) {
        toast.success(`تم شحن رصيدك! الرصيد الآن ${data.balance} ${UNIT}`);
        setCode('');
        fetchBalance();
      } else toast.error(data.detail || 'فشل شحن البطاقة');
    } catch { toast.error('فشل الاتصال بالخادم'); }
    setRedeeming(false);
  };

  const txnIcon = (t) => {
    if (t.type === 'gift') return <Gift className="w-4 h-4 text-pink-600" />;
    if (t.type === 'recharge' || t.type === 'adjust') return <ArrowUpCircle className="w-4 h-4 text-green-600" />;
    if (t.type === 'deduct' || t.type === 'purchase') return <ArrowDownCircle className="w-4 h-4 text-red-600" />;
    return <Clock className="w-4 h-4 text-amber-600" />;
  };

  return (
    <div className="space-y-4" dir="rtl">
      {/* Balance hero */}
      <Card className="border-[#E8DFCF] bg-gradient-to-l from-[#EF9F27] to-[#D88E1F] overflow-hidden">
        <CardContent className="p-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-2xl bg-amber-700 text-white flex items-center justify-center">
              <Wallet className="w-7 h-7" />
            </div>
            <div>
              <p className="text-2xl font-bold">رصيد التعلّم</p>
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-amber-700 mt-1" />
              ) : (
                <p className="text-3xl font-bold text-[#412402]">{balance} <span className="text-base font-normal text-[#412402]/60"></span></p>
              )}
            </div>
          </div>
          <Button variant="outline" size="sm" className='bg-white' onClick={fetchBalance}>
            <RefreshCw className="w-4 h-4 ml-1" /> تحديث
          </Button>
        </CardContent>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Request balance */}
        <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-[#412402] font-semibold">
              <Send className="w-4 h-4 text-amber-700" /> طلب شحن رصيد
            </div>

            {/* How-to-charge instructions */}
            <div className="rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-[11px] leading-5 text-amber-900">
              <p className="font-bold mb-1">كيف أشحن رصيدي؟</p>
              <ol className="list-decimal pr-4 space-y-0.5">
                <li>تواصل مع الدعم الفني لمعرفة طرق الدفع الإلكتروني</li>
                <li>أدخل القيمة التي حوّلتها بوحدة «{UNIT}».</li>
                <li>أدخل <b>الرقم المرجعي</b>: الرقم أو الاسم الذي حوّلت منه — حتى يتمكن الدعم من مطابقة حوالتك.</li>
                <li>اضغط «إرسال الطلب»، وسيصلك كود بطاقة شحن أدخله في «شحن بطاقة» المجاور.</li>
              </ol>
            </div>

            <div>
              <Label>القيمة المطلوبة (د.أ)</Label>
              <Input type="number" min={1} step="1" value={reqAmount}
                onChange={(e) => setReqAmount(e.target.value)} placeholder="مثال: 10" data-testid="request-amount-input" />
            </div>
            <div>
              <Label>الرقم المرجعي <span className="text-red-500">*</span></Label>
              <Input value={reqRef} onChange={(e) => setReqRef(e.target.value)}
                placeholder="الرقم أو الاسم الذي حوّلت منه" data-testid="request-reference-input" />
              {/* <p className="text-[11px] text-[#412402]/50 mt-1">مثال: 0791234567 أو اسم صاحب الحوالة.</p> */}
            </div>
            <div>
              <Label>ملاحظة (اختياري)</Label>
              <Input value={reqNote} onChange={(e) => setReqNote(e.target.value)} placeholder="مثال: لشراء بنك الأسئلة" />
            </div>
            <Button onClick={requestBalance} disabled={requesting} className="w-full bg-amber-700 hover:bg-amber-800" data-testid="send-request-btn">
              {requesting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <Send className="w-4 h-4 ml-2" />}
              إرسال الطلب للدعم الفني
            </Button>
          </CardContent>
        </Card>

        {/* Redeem card */}
        <Card className="border-[#E8DFCF]">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-[#412402] font-semibold">
              <CreditCard className="w-4 h-4 text-amber-700" /> شحن بطاقة
            </div>
            <p className="text-xs text-[#412402]/60">أدخل كود البطاقة الذي أرسله لك الدعم الفني لإضافة قيمتها إلى رصيدك.</p>
            <div>
              <Label>كود البطاقة</Label>
              <Input ref={redeemInputRef} value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="مثال: A1B2-C3D4-E5F6"
                className="font-mono tracking-widest text-center"
                data-testid="redeem-code-input" />
            </div>
            <Button onClick={redeemCard} disabled={redeeming} className="w-full bg-green-700 hover:bg-green-800" data-testid="redeem-card-btn">
              {redeeming ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <CreditCard className="w-4 h-4 ml-2" />}
              شحن الرصيد
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Transactions log */}
      <Card className="border-[#E8DFCF]">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 text-[#412402] font-semibold mb-3">
            <ScrollText className="w-4 h-4 text-amber-700" /> سجل الأرصدة
          </div>
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin text-amber-600 mx-auto" /></div>
          ) : transactions.length === 0 ? (
            <p className="text-center text-sm text-[#412402]/50 py-8">لا توجد حركات بعد.</p>
          ) : (
            <div className="space-y-1">
              {transactions.map((t) => (
                <div key={t.id} className="flex items-center gap-2 p-2 rounded-lg border border-[#E8DFCF] bg-white text-sm">
                  {txnIcon(t)}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[#412402]">{TXN_LABELS[t.type] || t.type}</p>
                    {t.note ? <p className="text-[11px] text-[#412402]/70 truncate">{t.note}</p> : null}
                    <p className="text-[11px] text-[#412402]/50">
                      {fmtDate(t.created_at)}
                      {t.code ? ` • كود ${t.code}` : ''}
                      {t.status === 'pending' ? ' • بانتظار موافقة الدعم الفني' : ''}
                    </p>
                  </div>
                  <Badge className={`shrink-0 ${(t.type === 'deduct' || t.type === 'purchase') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {(t.type === 'deduct' || t.type === 'purchase') ? '-' : (t.type === 'request' ? '' : '+')}{t.amount} {UNIT}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StudentWallet;
